import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors (logout)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  me: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email })
};

// Contact Form API (public)
export const contactAPI = {
  submit: (data) => axios.post(`${API_URL}/contact`, data)
};

// Leads APIs
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getOne: (id) => api.get(`/leads/${id}`),
  update: (id, data) => api.patch(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  addNote: (id, content) => api.post(`/leads/${id}/notes`, { content })
};

// Services APIs
export const servicesAPI = {
  getAll: (params) => api.get('/services', { params }),
  getOne: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.patch(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`)
};

// Analytics APIs
export const analyticsAPI = {
  getOverview: (params) => api.get('/analytics/overview', { params })
};

// Orders APIs
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getOne: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.patch(`/orders/${id}`, data),
  addNote: (id, content, type) => api.post(`/orders/${id}/notes`, { content, type }),
  getStats: () => api.get('/orders/stats/overview'),
  getAIQuoteSuggestion: (orderData) => api.post('/orders/ai-quote-suggestion', orderData)
};

// Content Management APIs
export const contentAPI = {
  getAll: (params) => api.get('/content', { params }),
  createOrUpdate: (data) => api.post('/content', data),
  delete: (id) => api.delete(`/content/${id}`)
};

// Settings APIs
export const settingsAPI = {
  getAll: (params) => api.get('/settings', { params }),
  update: (category, settings) => api.post('/settings', { category, settings })
};

// Customers (CRM) APIs
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  get360: (id) => api.get(`/customers/${id}/360`),
  update: (id, data) => api.patch(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  addNote: (id, content) => api.post(`/customers/${id}/notes`, { content }),
  getActivities: (id, params) => api.get(`/customers/${id}/activities`, { params }),
  exportCsv: () => api.get('/customers/export/csv', { responseType: 'blob' }),
  getSavedViews: () => api.get('/customers/saved-views'),
  createSavedView: (data) => api.post('/customers/saved-views', data),
  deleteSavedView: (id) => api.delete(`/customers/saved-views/${id}`)
};

// Tasks APIs
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getToday: () => api.get('/tasks/today'),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`)
};

// Opportunities APIs
export const opportunitiesAPI = {
  getAll: (params) => api.get('/opportunities', { params }),
  getOne: (id) => api.get(`/opportunities/${id}`),
  getForecast: () => api.get('/opportunities/forecast'),
  create: (data) => api.post('/opportunities', data),
  update: (id, data) => api.patch(`/opportunities/${id}`, data),
  delete: (id) => api.delete(`/opportunities/${id}`)
};

// Email Templates APIs
export const emailTemplatesAPI = {
  getAll: () => api.get('/email-templates'),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.patch(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
  seed: () => api.post('/email-templates/seed')
};

// Email send/threads (Phase 5)
export const emailAPI = {
  send: (data) => api.post('/email/send', data),
  getThreads: (customerId) => api.get(`/email/threads/${customerId}`)
};

// GDPR (Phase 6)
export const gdprAPI = {
  exportCustomer: (id) => api.get(`/gdpr/customers/${id}/export`, { responseType: 'blob' }),
  deleteCustomer: (id, reason) => api.delete(`/gdpr/customers/${id}`, { data: { reason } })
};

// Audit Log (Phase 6)
export const auditAPI = {
  getAll: (params) => api.get('/audit', { params })
};

// Customer Portal (Phase 6) — passwordless magic link
export const portalAPI = {
  requestLink: (email) => api.post('/portal/request-link', { email }),
  exchange: (token) => api.post('/portal/exchange', { token }),
  me: () => api.get('/portal/me', { headers: { Authorization: `Bearer ${localStorage.getItem('portalToken') || ''}` } }),
  myOrders: () => api.get('/portal/me/orders', { headers: { Authorization: `Bearer ${localStorage.getItem('portalToken') || ''}` } }),
  myQuotes: () => api.get('/portal/me/quotes', { headers: { Authorization: `Bearer ${localStorage.getItem('portalToken') || ''}` } }),
  myInvoices: () => api.get('/portal/me/invoices', { headers: { Authorization: `Bearer ${localStorage.getItem('portalToken') || ''}` } })
};

// Intelligence / AI (Phase 8)
export const intelligenceAPI = {
  getDuplicates: () => api.get('/intelligence/duplicates'),
  mergeDuplicates: (primaryId, mergedIds) => api.post('/intelligence/duplicates/merge', { primaryId, mergedIds }),
  getNextActions: () => api.get('/intelligence/next-actions'),
  getAtRisk: () => api.get('/intelligence/at-risk'),
  scoreNow: (customerId) => api.post(`/intelligence/score-now/${customerId}`)
};

// Analytics (Phase 7) — adds forecast/funnel/cohort/ltv to existing analytics
export const analyticsForecastAPI = {
  forecast: () => api.get('/analytics/forecast'),
  funnel: (days = 30) => api.get('/analytics/funnel', { params: { days } }),
  cohort: (months = 6) => api.get('/analytics/cohort', { params: { months } }),
  ltv: () => api.get('/analytics/ltv'),
  segments: () => api.get('/analytics/segments')
};


// Malta Track C — admin surfaces
export const complianceAPI = {
  getAll: (params) => api.get('/compliance', { params }),
  getOne: (id) => api.get(`/compliance/${id}`),
  create: (data) => api.post('/compliance', data),
  update: (id, data) => api.patch(`/compliance/${id}`, data),
  delete: (id) => api.delete(`/compliance/${id}`)
};

export const screensAPI = {
  getAll: (params) => api.get('/screens', { params }),
  getOne: (id) => api.get(`/screens/${id}`),
  create: (data) => api.post('/screens', data),
  update: (id, data) => api.patch(`/screens/${id}`, data),
  delete: (id) => api.delete(`/screens/${id}`)
};

export const partnersAPI = {
  getAll: (params) => api.get('/partners', { params }),
  getOne: (id) => api.get(`/partners/${id}`),
  create: (data) => api.post('/partners', data),
  update: (id, data) => api.patch(`/partners/${id}`, data),
  delete: (id) => api.delete(`/partners/${id}`)
};

export const partFilesAPI = {
  getAll: (params) => api.get('/part-files', { params }),
  getOne: (id) => api.get(`/part-files/${id}`),
  create: (data) => api.post('/part-files', data),
  update: (id, data) => api.patch(`/part-files/${id}`, data),
  delete: (id) => api.delete(`/part-files/${id}`)
};

export const engagementsAPI = {
  getAll: (params) => api.get('/engagements', { params }),
  getOne: (id) => api.get(`/engagements/${id}`),
  create: (data) => api.post('/engagements', data),
  update: (id, data) => api.patch(`/engagements/${id}`, data),
  delete: (id) => api.delete(`/engagements/${id}`)
};

export default api;
